import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AuthMe } from '@/lib/api';

export function useAuth() {
  const qc = useQueryClient();

  const meQuery = useQuery<AuthMe>({
    queryKey: ['auth', 'me'],
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: true,
  });

  const loginMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.auth.login(username, password),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: async () => {
      qc.setQueryData(['auth', 'me'], { authenticated: false });
      qc.clear();
    },
  });

  return {
    isLoading: meQuery.isLoading,
    isAuthenticated: meQuery.data?.authenticated === true,
    user: meQuery.data?.user,
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error as Error | null,
    isLoggingIn: loginMutation.isPending,
    logout: logoutMutation.mutate,
  };
}
